import { ref, computed } from 'vue';

export const ANIMATION_STATUS = {
	PENDING: 'pending',
	EMPTY: 'empty',
	COMPLETED: 'completed'
};

/**
 * 动画工作流
 * - 动画是否准备就绪，准备就绪后才允许播放动画
 * - 动画只会在内容可见后才会开始播放
 * - 动画只会播放一次
 * - 如果不满足播放条件，则不会运行动画，直接渲染缺省样式
 * @param {Function} createAnimation 创建动画实例
 */
export const useAnimationWorkflow = ({ createAnimation }) => {
	if (!createAnimation) {
		console.error('createAnimation is required');
	}

	const visible = ref(false);
	const played = ref(false);
	const animInstance = ref(null);

	const status = ref(ANIMATION_STATUS.PENDING);
	const setStatus = newStatus => {
		status.value = newStatus;
	};

	const isEmptyAnim = computed(() => status.value === ANIMATION_STATUS.EMPTY);
	const isCompletedAnim = computed(() => status.value === ANIMATION_STATUS.COMPLETED);
	const isPendingAnim = computed(() => status.value === ANIMATION_STATUS.PENDING);

	/**
	 * 初始化动画实例
	 */
	const ensureAnimation = () => {
		if (animInstance.value) return;
		animInstance.value = createAnimation?.();
	};

	/**
	 * 尝试运行动画
	 */
	const tryRun = () => {
		if (!visible.value) return; // 当前模块是否可见
		if (status.value !== ANIMATION_STATUS.COMPLETED) return; // 数据是否落地（有无数据）
		if (played.value) return; // 动画是否已经播放过（只播放一次）

		play();
		played.value = true;
	};
	const play = () => {
		ensureAnimation();
		const instance = animInstance.value;
		instance?.play?.();
	};

	/**
	 * 标记是否准备好播放动画
	 * @param {boolean} hasData 是否有数据
	 */
	const markAnimReady = hasData => {
		setStatus(hasData ? ANIMATION_STATUS.COMPLETED : ANIMATION_STATUS.EMPTY);
		tryRun();
	};

	/**
	 * 标记容器是否可见
	 * @param {boolean} val 是否可见
	 */
	const markContentVisible = val => {
		visible.value = val;
		tryRun();
	};

	const reset = () => {
		played.value = false;
	};

	return {
		visible,
		played,
		status,

		isEmptyAnim,
		isCompletedAnim,
		isPendingAnim,

		ensureAnimation,
		tryRun,
		markAnimReady,
		markContentVisible,
		reset
	};
};
